import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {Subject, take, takeUntil} from 'rxjs';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';
import {iResponse} from '../../../models/response/response.model';
import {CawawiArticle, CawawiArticlePagingModel} from '../../../models/cawawi/cawawi.article';
import {PagingModel} from '../../../models/paging/paging.model';
import {CawawiArticleQueryModel, FilterArticleQuery} from '../../../models/cawawi/cawawi-article-query.model';
import {PatientModel} from '../../../models/patient/patient.model';
import {CawawiService} from '../../../services/cawawi/cawawi.service';
import {RecipeService} from '../../../services/recipe/recipe.service';
import {Router} from '@angular/router';
import {RecipeModel, UpdateRecipeModel} from '../../../models/recipe/recipe.model';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import {MatInput} from '@angular/material/input';
import {InfoMessageService} from '../../../services/infomessage/infomessage.service';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatSliderModule} from '@angular/material/slider';

@Component({
    selector: 'app-following-prescription',
    imports: [
    DecimalPipe,
    FormsModule,
    MatInput,
    MatPaginator,
    ReactiveFormsModule,
    CurrencyPipe,
    MatCheckbox,
    MatSliderModule
],
    templateUrl: './following-prescription.component.html',
    styleUrl: './following-prescription.component.sass'
})
export class FollowingPrescriptionComponent implements OnInit, OnDestroy {
    @ViewChild(MatPaginator, {static: false}) paginator?: MatPaginator;
    private destroy$ = new Subject<void>();
    filterTimeout: any
    prescriptionForm: FormGroup = new FormGroup({
        selectedArticles: new FormArray([]),
        articles: new FormArray([])
    });
    filterControl = new FormControl('');
    cawawiArticles: CawawiArticle[] = [];
    selectedArticles: CawawiArticle[] = [];
    articleFilterQuery: FilterArticleQuery = {
        page: 1,
        perPage: 10,
        filter: '',
        orderBy: 'articleNumber',
        order: 'ASC',
        onlyAvailable: true,
        detailed: true,
        thcMin: null,
        thcMax: null,
        cbdMin: null,
        cbdMax: null,
        priceMin: null,
        priceMax: null,
        characteristics: null,
        characteristicsAllTogether: null,
        conditions: null,
        conditionsAllTogether: null,
        supplier: null
    }
    actualPaging!: PagingModel;
    patient!: PatientModel;
    prescription!: RecipeModel

    loadedRecipe!: UpdateRecipeModel;
    showOnlyAvailableArticles: boolean = true

    cbdMin: number = 0;
    cbdMax: number = 30;
    thcMin: number = 0;
    thcMax: number = 30;
    priceMin: number = 0;
    priceMax: number = 30;

    constructor(
        private fb: FormBuilder,
        private cawawiService: CawawiService,
        private recipeService: RecipeService,
        private router: Router,
        private infoMessageService: InfoMessageService
    ) {
        const navigation = this.router.currentNavigation();
        const state = navigation?.extras?.state;
        const patient = state?.['patient'];
        const prescription = state?.['recipe'];
        if (patient) {
            this.patient = patient
        }

        if (prescription) {
            this.compareRecipe(prescription)
            this.prescription = prescription
            this.selectedArticles = this.prescription.positions
        }
    }

    ngOnInit(): void {
        this.getCawawiArticles(false, true);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    applyFilter(force: boolean = false): void {
        if (!force && this.filterControl.value === this.articleFilterQuery.filter) {
            return;
        }

        clearTimeout(this.filterTimeout);
        this.filterTimeout = setTimeout(() => {
            if (this.filterControl.value !== this.articleFilterQuery.filter) {
                this.articleFilterQuery.page = 1;
                this.articleFilterQuery.filter = this.filterControl.value || '';
            }
            this.getCawawiArticles(true);
        }, 500)
    }
    changeAvailableState(): void {
        this.articleFilterQuery.onlyAvailable = this.showOnlyAvailableArticles;
        this.articleFilterQuery.page = 1;
        this.getCawawiArticles(true);
    }

    onCbdChange(): void {
        this.articleFilterQuery.cbdMin = this.cbdMin;
        this.articleFilterQuery.cbdMax = this.cbdMax;
        this.articleFilterQuery.page = 1;
        this.applyFilter(true);
    }

    onThcChange(): void {
        this.articleFilterQuery.thcMin = this.thcMin;
        this.articleFilterQuery.thcMax = this.thcMax;
        this.articleFilterQuery.page = 1;
        this.applyFilter(true);
    }

    onPriceChange(): void {
        this.articleFilterQuery.priceMin = this.priceMin;
        this.articleFilterQuery.priceMax = this.priceMax;
        this.articleFilterQuery.page = 1;
        this.applyFilter(true);
    }

    onPageChange(event: PageEvent): void {
        if (event.pageIndex === this.actualPaging.totalPages - 1) {
            this.articleFilterQuery.page = this.actualPaging.totalPages;
            this.articleFilterQuery.perPage = event.pageSize;
            this.getCawawiArticles(true)
            return;
        }
        if (event.pageIndex === 0) {
            this.articleFilterQuery.page = 1;
            this.articleFilterQuery.perPage = event.pageSize;
            this.getCawawiArticles(true)
            return;
        }
        if (event.previousPageIndex! > event.pageIndex) {
            this.articleFilterQuery.page = this.actualPaging.pageNumber - 1;
            this.articleFilterQuery.perPage = event.pageSize;
            this.getCawawiArticles(true)
            return;
        }
        if (event.previousPageIndex! < event.pageIndex) {
            this.articleFilterQuery.page = this.actualPaging.pageNumber + 1;
            this.articleFilterQuery.perPage = event.pageSize;
            this.getCawawiArticles(true)
            return;
        }

        if (event.pageSize !== this.actualPaging.pageSize) {
            this.articleFilterQuery.page = event.pageIndex + 1;
            this.articleFilterQuery.perPage = event.pageSize;
            this.getCawawiArticles(true)
            return;
        }
    }

    updateSelectedArticles(ctrl: AbstractControl): void {
        const selectedArticlesArray = this.selectedArticlesFormArray;
        const articlesArray = this.articlesFormArray;
        const articleNumber = ctrl.get('articleNumber')?.value;
        const quantityToAdd = ctrl.get('quantityToAdd')?.value;

        // Check if this article is already in the selectedArticles array.
        const index = selectedArticlesArray.controls.findIndex(group =>
            group.get('articleNumber')?.value === articleNumber
        );

        const indexInArticles = articlesArray.controls.findIndex(group =>
            group.get('articleNumber')?.value === articleNumber
        );

        if (index !== -1) {
            // Update the entry: if the quantity is set to 0, remove it.
            if (quantityToAdd === 0) {
                selectedArticlesArray.removeAt(index);
                ctrl.get('inRecipe')?.patchValue(false);
            } else {
                selectedArticlesArray.at(index).patchValue({
                    quantity: quantityToAdd,
                    quantityToAdd: quantityToAdd,
                    inRecipe: true
                });
            }
        } else {
            // Only add if a positive quantity is provided.
            if (quantityToAdd > 0) {
                const articleGroup = this.fb.group({
                    articleNumber: [ctrl.get('articleNumber')?.value],
                    articleName: [ctrl.get('articleName')?.value],
                    unitOfMeasurement: [ctrl.get('unitOfMeasurement')?.value],
                    nettoPrice: [ctrl.get('nettoPrice')?.value],
                    bindingSize: [ctrl.get('bindingSize')?.value],
                    quantityToAdd: [quantityToAdd],
                    quantity: [quantityToAdd],
                    variantName: [ctrl.get('variantName')?.value],
                    inRecipe: [true],
                    drugDosage: [ctrl.get('drugDosage')?.value],
                    drugProcessing: [ctrl.get('drugProcessing')?.value]
                });
                selectedArticlesArray.push(articleGroup);
                // Mark the current control as being in the recipe.
                articlesArray.removeAt(indexInArticles);
                ctrl.get('inRecipe')?.patchValue(true);
            }
        }
    }

    removeSelectedArticle(index: number, articleControl: AbstractControl): void {
        this.selectedArticlesFormArray.removeAt(index);
        this.articlesFormArray.push(articleControl);
    }

    prescriptionReview(): void {
        this.prescription.positions = this.selectedArticlesFormArray.value;
        this.setPrescriptionReview(this.prescription)

    }

    private initForm(initialLoad: boolean): void {
        if (this.prescription && initialLoad) {
            this.articlesFormArray.controls = this.cawawiArticles.map(article => this.createArticleGroup(article))
            this.selectedArticlesFormArray.controls = this.prescription.positions.map(article => this.createSelectedArticleGroup(article))
        } else {
            this.articlesFormArray.controls = this.cawawiArticles.map(article => this.createArticleGroup(article))
        }
    }

    private createArticleGroup(article: CawawiArticle): FormGroup {
        return this.fb.group({
            articleNumber: [article.articleNumber],
            articleName: [article.articleName],
            unitOfMeasurement: 1,
            nettoPrice: [article.nettoPrice],
            bindingSize: [article.bindingSize],
            quantity: [article.quantity],
            variantName: [article.variantName],
            quantityToAdd: [0, [Validators.required]],
            inRecipe: [article.inRecipe],
            conditions: [article.conditions],
            genetic: [article.genetic],
            thc: [article.thc],
            cbd: [article.cbd],
            drugDosage: [''],
            drugProcessing: ['']
        });
    }

    private createSelectedArticleGroup(article: CawawiArticle): FormGroup {
        return this.fb.group({
            articleNumber: [article.articleNumber],
            articleName: [article.articleName],
            unitOfMeasurement: 1,
            nettoPrice: [article.nettoPrice],
            bindingSize: [article.bindingSize],
            quantity: [article.quantity],
            variantName: [article.variantName],
            quantityToAdd: [article.quantity, [Validators.required]],
            inRecipe: [article.inRecipe],
            conditions: [article.conditions],
            genetic: [article.genetic],
            thc: [article.thc],
            cbd: [article.cbd],
            drugDosage: [article.drugDosage ? article.drugDosage : ''],
            drugProcessing: [article.drugProcessing ? article.drugProcessing : '']
        });
    }

    private compareRecipe(recipe: RecipeModel): void {
        this.loadedRecipe = {
            articles: recipe.positions,
            id: recipe.id
        }
    }

    private getCawawiArticles(pageChange: boolean = false, firstLoad: boolean = false): void {
        this.cawawiService.postGetCawawiArticle(this.articleFilterQuery)
            // TODO i dont know why this is not working with destroy...
            // .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<CawawiArticlePagingModel>) => {
                    this.cawawiArticles = [...response.data.articles];
                    this.actualPaging = response.data.paging;
                    this.initForm(firstLoad);
                    if (pageChange) {
                        this.compareArticles();
                    }
                    if (this.prescription && firstLoad) {
                        this.compareArticles();
                    }
                },
                error: (error) => {
                }
            })
    }

    private compareArticles(): void {
        this.selectedArticlesFormArray.controls.forEach(selectedArticle => {
            const index = this.articlesFormArray.controls.findIndex(control =>
                control.value.articleNumber === selectedArticle.value.articleNumber
            );

            if (index !== -1) {
                this.articlesFormArray.removeAt(index);
            }
        });
    }


    private setPrescriptionReview(prescription: RecipeModel): void {
        this.updatePrescription(prescription);

    }

    private updatePrescription(prescription: RecipeModel): void {
        const update: UpdateRecipeModel = {
            id: prescription.id,
            articles: prescription.positions

        }
        this.recipeService.updateRecipe(prescription.id, update)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.prescriptionReviewForDoctor(prescription.id);
                },
                error: (error) => {
                }
            })
    }

    private prescriptionReviewForDoctor(prescriptionId: string) {
        this.recipeService.notifyForDoctorReview(prescriptionId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.infoMessageService.createMessage('Das Folgerezept wurde an den Arzt zur Freigabe gesendet', 'success')
                    this.router.navigate(['meine-akte']);
                },
                error: (error) => {
                }
            })

    }

    getTotalPrice(): number {
        let total = 0;
        this.selectedArticlesFormArray.controls.forEach(article => {
            total += article.value.nettoPrice * article.value.quantity;
        });
        return total;
    }


    get articlesFormArray(): FormArray {
        return this.prescriptionForm.get('articles') as FormArray;
    }

    get selectedArticlesFormArray(): FormArray {
        return this.prescriptionForm.get('selectedArticles') as FormArray;
    }

}
